<script lang="ts" setup>
import { VaultAssetInstallationEntityType } from '~/codegen/graphql'
import useDashboardIcon from '~/composables/use-dashboard-icon'
import useCodeSourceConnection from '~/composables/use-code-source-connection'
import useSkillNavigationContext from '~/composables/use-skill-navigation-context'
import { GetSkillsDashboardsDocument } from '~/pages/usage/dashboards/[dashboardId]/index.generated'

const { enabled: pulseFeaturesEnabled } = useFlag('PULSE_FEATURES')
const { enabled: skillsEnabled } = useFlag('SLEUTH_SKILLS')
const { enabled: aiScanEnabled } = useFlag('AI_SCAN')
const { user } = useCurrentUser()
const { isAnyCodeSourceConnected } = useCodeSourceConnection()

const route = useRoute()
const { context: skillNavContext } = useSkillNavigationContext()

const showSearch = computed(() => pulseFeaturesEnabled.value || skillsEnabled.value)

// Check if we're on a skill detail page with a single installation context
const hasSkillContext = computed(() => skillNavContext.value.entityType !== null)

const isAllSkillsActive = computed(() => {
  // If we have a skill context (single installation), don't highlight "All Skills"
  if (hasSkillContext.value) return false
  return route.path === '/assets' || route.path.startsWith('/assets/')
})
const isInstalledSkillsActive = computed(() => {
  if (!user.value?.id) return false
  return route.path === `/users/${user.value.id}/assets/installed`
})
const isChangeRequestsActive = computed(() => route.path.startsWith('/change-requests'))
const isCollectionsActive = computed(() => route.path === '/collections' || route.path.startsWith('/collections/'))
const isDataCollectionActive = computed(() => route.path === '/aiscan/data-collection')
const isEpisodesActive = computed(() => route.path === '/aiscan/episodes')
const isAiScanActive = computed(
  () =>
    (route.path === '/aiscan' || route.path.startsWith('/aiscan/')) &&
    !isDataCollectionActive.value &&
    !isEpisodesActive.value,
)
const isOrgSkillsActive = computed(() => {
  // Also active when viewing a skill installed only at org level
  if (skillNavContext.value.entityType === VaultAssetInstallationEntityType.Organization) return true
  return route.path === '/organization' || route.path.startsWith('/organization/')
})

const isTeamModalOpen = ref(false)
const isBotModalOpen = ref(false)
const isInstallationModalOpen = ref(false)
const isMySkillsActive = computed(() => {
  if (!user.value?.id) return false
  // Also active when viewing a skill installed only at user/personal level
  if (skillNavContext.value.entityType === VaultAssetInstallationEntityType.User) return true
  return route.path === `/users/${user.value.id}/assets` && !route.path.includes('/installed')
})

const installedSkillsUrl = computed(() => (user.value?.id ? `/users/${user.value.id}/assets/installed` : '/assets'))
const mySkillsUrl = computed(() => (user.value?.id ? `/users/${user.value.id}/assets` : '/assets'))

const { data: dashboardsData } = useQuery({
  query: GetSkillsDashboardsDocument,
  tags: ['skills-dashboards'],
  cachePolicy: 'cache-and-network',
})
const dashboards = computed(() => dashboardsData.value?.organization.skillsDashboards ?? [])
const { getDashboardIcon } = useDashboardIcon()
</script>

<template>
  <nav class="z-10 flex min-h-0 max-w-full flex-1 flex-col bg-blue-50 py-2 text-nav dark:bg-gray-950">
    <NavigationHeader class="mb-4" />

    <NavigationOnboardingStatus class="mb-4" />

    <div v-if="showSearch" class="mb-4 flex items-center gap-1 px-2">
      <NavigationSearch />
    </div>

    <div class="flex flex-1 flex-col overflow-y-auto">
      <template v-if="skillsEnabled">
        <NavigationSectionLabel label="Manage" />
        <div class="flex flex-col px-2">
          <NavigationItem to="/assets?reset=true" icon="light:book" label="AI Assets" :is-active="isAllSkillsActive" />
          <NavigationItem
            to="/collections"
            icon="light:layer-group"
            label="Collections"
            :is-active="isCollectionsActive"
          />
          <NavigationItem
            to="/change-requests?reset=true"
            icon="light:code-compare"
            label="Change Requests"
            :is-active="isChangeRequestsActive"
          />
        </div>
      </template>

      <template v-if="skillsEnabled">
        <NavigationSectionLabel label="Distribute" />
        <div class="flex flex-col px-2">
          <NavigationItem
            :to="installedSkillsUrl"
            icon="light:download"
            label="Installed"
            :is-active="isInstalledSkillsActive"
          />
          <NavigationItem
            to="/organization"
            icon="light:building"
            label="Organization"
            :is-active="isOrgSkillsActive"
          />
          <NavigationGoToItem
            label="Bots"
            icon="light:robot"
            object-type="bot"
            show-create-button
            @create="isBotModalOpen = true"
          />
          <NavigationGoToItem
            label="Teams"
            icon="light:users"
            object-type="team"
            show-create-button
            @create="isTeamModalOpen = true"
          />
          <NavigationGoToItem
            v-if="isAnyCodeSourceConnected"
            label="Repositories"
            icon="light:code"
            object-type="repository"
          />
          <NavigationItem :to="mySkillsUrl" icon="light:user" label="Personal" :is-active="isMySkillsActive" />
        </div>
      </template>

      <template v-if="skillsEnabled">
        <NavigationSectionLabel label="Govern" />
        <div class="flex flex-col px-2">
          <NavigationItem
            to="/usage/audit-log"
            icon="light:clipboard-list"
            label="Audit Log"
            :is-active="route.path === '/usage/audit-log'"
          />
          <NavigationItem
            v-for="dashboard in dashboards"
            :key="dashboard.id"
            :to="`/usage/dashboards/${dashboard.id}`"
            :icon="getDashboardIcon(dashboard.name)"
            :label="dashboard.name"
            :is-active="route.path === `/usage/dashboards/${dashboard.id}`"
          />
        </div>
      </template>

      <template v-if="aiScanEnabled">
        <NavigationSectionLabel label="AI Insights" />
        <div class="flex flex-col px-2">
          <NavigationItem to="/aiscan" icon="light:heart-pulse" label="Reports" :is-active="isAiScanActive" />
          <NavigationItem
            to="/aiscan/episodes"
            icon="light:cloud-arrow-up"
            label="Data collection"
            :is-active="isEpisodesActive"
          />
        </div>
      </template>

      <template v-if="pulseFeaturesEnabled">
        <NavigationSectionLabel label="Other" />
        <div class="flex flex-col px-2">
          <NavigationGoToItem
            label="Teamspaces"
            icon="light:folder"
            object-type="teamspace"
            show-create-button
            @create="navigateTo({ name: 'teamspace-new' })"
          />
        </div>
      </template>

      <div class="mb-8 mt-auto" />

      <div class="flex flex-col px-2">
        <NavigationItem
          v-if="aiScanEnabled || skillsEnabled"
          icon="light:wrench"
          label="How to set up"
          @click="isInstallationModalOpen = true"
        />
        <NavigationHelp />
        <NavigationUserMenu />
      </div>
    </div>

    <NavigationCreateTeamModal v-model="isTeamModalOpen" />
    <NavigationCreateBotModal v-model="isBotModalOpen" />
    <NavigationInstallationModal v-model="isInstallationModalOpen" />
  </nav>
</template>
